import { useEffect, useState, type PropsWithChildren } from "react";
import { canReloadFor, findNewDeployedVersion, reloadForNewBuild } from "../../utils/appVersion";
import UpdateSplash from "./UpdateSplash";

export const VERSION_CHECK_INTERVAL = 60_000;

const WAKE_EVENTS = ["focus", "online"] as const;

/**
 * Polls version.json and moves the tab onto a new deploy before the user clicks into a
 * chunk the deploy deleted. Must wrap the top-level error boundary: a caught crash would
 * otherwise unmount the poller and leave the tab on the error screen.
 */
const VersionCheck = ({ children }: PropsWithChildren) => {
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let isChecking = false;

    const checkForNewVersion = async () => {
      // The interval and the wake-up events overlap; one check at a time is enough.
      if (isChecking) return;
      isChecking = true;
      try {
        const next = await findNewDeployedVersion();
        // A refused reload leaves the app usable on the build it has, without the splash.
        if (!next || !isMounted || !canReloadFor(next)) return;

        setIsUpdating(true);
        const didReload = await reloadForNewBuild(next);
        if (!didReload && isMounted) setIsUpdating(false);
      } finally {
        isChecking = false;
      }
    };

    // Background tabs throttle timers, so also check the moment the tab is used again.
    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") void checkForNewVersion();
    };

    void checkForNewVersion();
    const intervalId = window.setInterval(() => void checkForNewVersion(), VERSION_CHECK_INTERVAL);
    document.addEventListener("visibilitychange", checkWhenVisible);
    WAKE_EVENTS.forEach((type) => window.addEventListener(type, checkWhenVisible));

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", checkWhenVisible);
      WAKE_EVENTS.forEach((type) => window.removeEventListener(type, checkWhenVisible));
    };
  }, []);

  return (
    <>
      {isUpdating && <UpdateSplash />}
      {children}
    </>
  );
};

export default VersionCheck;
